import { supabaseClient } from "@/lib/supabase";
import {
    DEFAULT_LOCALE,
    normaliseLocaleTag,
    type ConsentTranslationRow,
    type SupportedLocale,
} from "@patient-watch/i18n";
import type { ConsentViewT } from "@/types/database/consents";
import {
    applyUnsignedConsentOverlay,
    consentHasSignedCopy,
    type ConsentBodyCopy,
} from "../utils/applyUnsignedConsentOverlay";

export { applyUnsignedConsentOverlay, consentHasSignedCopy };

async function fetchConsentBodyById(consentBodyId: string): Promise<ConsentBodyCopy | null> {
    const { data, error } = await supabaseClient
        .from("consent_bodies")
        .select("consent_body_id, consent_name, summary_text, attestation_text, document_url")
        .eq("consent_body_id", consentBodyId)
        .maybeSingle();

    if (error) throw error;
    return data;
}

export async function fetchConsentBodyTranslation(
    consentBodyId: string,
    locale: SupportedLocale
): Promise<ConsentTranslationRow | null> {
    const normalizedLocale = normaliseLocaleTag(locale) ?? DEFAULT_LOCALE;
    if (normalizedLocale === DEFAULT_LOCALE) {
        return null;
    }

    const { data, error } = await supabaseClient
        .from("consent_body_translations")
        .select("consent_name, summary_text, attestation_text, document_url")
        .eq("consent_body_id", consentBodyId)
        .eq("locale", normalizedLocale)
        .maybeSingle();

    if (error) throw error;
    return data;
}

async function resolveConsentBodySource(consent: ConsentViewT): Promise<ConsentBodyCopy | null> {
    if (!consent.consent_body_id) {
        return null;
    }

    return fetchConsentBodyById(consent.consent_body_id);
}

export async function localizeUnsignedConsent(consent: ConsentViewT, locale: SupportedLocale): Promise<ConsentViewT> {
    if (consentHasSignedCopy(consent)) {
        return consent;
    }

    const body = await resolveConsentBodySource(consent);
    const translation =
        body && locale !== DEFAULT_LOCALE ? await fetchConsentBodyTranslation(body.consent_body_id, locale) : null;

    return applyUnsignedConsentOverlay(consent, locale, body, translation);
}

export async function localizeUnsignedConsents(
    consents: ConsentViewT[],
    locale: SupportedLocale
): Promise<ConsentViewT[]> {
    return Promise.all(consents.map((consent) => localizeUnsignedConsent(consent, locale)));
}
